import { localStore } from 'tradera/utils/storage';

// Remember to add prefix+key to STORAGE_KEYS in config/storage!
const CACHE_PREFIX = 'cache.';
const CACHE_KEYS = {
    SYSTEM_MESSAGES: 'systemMessages',
    SYSTEM_BADGES: 'systemBadges',
};
const DEFAULT_TTL = 10 * 60 * 1000;

class CacheHandler {
    constructor(store, keyPrefix) {
        this.store = store;
        this.keyPrefix = keyPrefix;
    }

    /**
     * Read item from the cache, return null if expired or non existent
     *
     * @param {string} key
     * @returns {null|*}
     */
    read(key) {
        const cached = this.store.getItem(this.keyPrefix + key);
        if (cached) {
            if (cached.expires > Date.now()) {
                return cached.data;
            } else {
                this.store.removeItem(this.keyPrefix + key);
            }
        }
        return null;
    }

    /**
     * Write an item to the cache with expiry time
     *
     * @param {string} key
     * @param data
     * @param {int} [ttl]
     * @returns {CacheHandler}
     */
    write(key, data, ttl = DEFAULT_TTL) {
        this.store.setItem(this.keyPrefix + key, {
            expires: Date.now() + ttl,
            data,
        });
        return this;
    }

    /**
     * Remove item regardless of expiry time
     *
     * @param {string} key
     * @returns {CacheHandler}
     */
    remove(key) {
        this.store.removeItem(this.keyPrefix + key);
        return this;
    }

    /**
     * Remove all items regardless of expiry time
     *
     * @returns {CacheHandler}
     */
    removeAll() {
        this.store.clearByPrefix(this.keyPrefix);
        return this;
    }

    /**
     * Remove item if expired, keep otherwise
     *
     * @param {string} key
     * @returns {CacheHandler}
     */
    expire(key) {
        const cached = this.store.getItem(this.keyPrefix + key);
        if (cached) {
            if (cached.expires <= Date.now()) {
                this.store.removeItem(this.keyPrefix + key);
            }
        }
        return null;
    }

    /**
     * Remove expired items
     *
     * @returns {CacheHandler}
     */
    expireAll() {
        const caches = this.store.getItemsByPrefix(this.keyPrefix);
        const now = Date.now();
        for (const [key, cached] of Object.entries(caches)) {
            if (cached.expires <= now) {
                this.store.removeItem(key);
            }
        }
        return this;
    }
}

// Standard cache with GDPR-compliant localStore and standard cache prefix
const cache = new CacheHandler(localStore, CACHE_PREFIX);

export { CACHE_KEYS, CacheHandler, cache };
